/**
 * Imports a known list of Instagram handles into a campaign's outreach (the
 * Pool's "Send to outreach"): preview classifies them against the roster and
 * writes nothing; commit starts the import batch the outreach worker runs.
 */
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OutreachHandlePreview, OutreachImportBatch } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import { commitOutreachHandles, fetchOutreachImport, previewOutreachHandles } from '../api/outreachApi';
import { OUTREACH_IMPORT_POLL_MS, startBoundedPoll } from './useImportWizard';

const isFinished = (batch: OutreachImportBatch) => batch.status === 'done' || batch.status === 'failed';

/** The batch is worked after the commit returns, so the Outreach counts are only
 *  right once it finishes. The follow is not tied to the Pool: the admin often
 *  leaves it for Outreach before then. Failed reads are retried until the poll
 *  gives up, which refreshes anyway. */
function refreshWhenFinished(campaignId: string, importId: number, refresh: () => void, onFinished?: (batch: OutreachImportBatch) => void) {
  startBoundedPoll({
    intervalMs: OUTREACH_IMPORT_POLL_MS,
    check: async () => {
      const batch = await fetchOutreachImport(campaignId, importId);
      const finished = isFinished(batch);
      if (finished) {
        refresh();
        onFinished?.(batch);
      }
      return finished;
    },
    onStalled: refresh,
    onRepeatedFailure: () => undefined,
    onRecovered: () => undefined,
  });
}

/** `onFinished` gets the batch's own result (created / matched / skipped), which the preview cannot know. */
export function useOutreachHandleImport(campaignId: string, onFinished?: (batch: OutreachImportBatch) => void) {
  const queryClient = useQueryClient();
  // One key per previewed list, so a retried or double-clicked commit replays
  // the same batch instead of importing the list twice.
  const commitKey = useRef<{ preview: OutreachHandlePreview; key: string } | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    void queryClient.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
  };

  const preview = useMutation<OutreachHandlePreview, Error, string[]>({
    mutationFn: (handles) => previewOutreachHandles(campaignId, handles),
  });

  const commit = useMutation<{ importId: number }, Error, OutreachHandlePreview>({
    mutationFn: (previewed) => {
      const held =
        commitKey.current?.preview === previewed
          ? commitKey.current
          : { preview: previewed, key: crypto.randomUUID() };
      commitKey.current = held;
      return commitOutreachHandles(campaignId, previewed, held.key);
    },
    onSuccess: ({ importId }) => {
      refresh();
      refreshWhenFinished(campaignId, importId, refresh, onFinished);
    },
  });

  return { preview, commit };
}
